import React, { useState, useMemo } from 'react';
import { 
  FaSearch,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaUniversity,
  FaFilter,
  FaStar,
  FaGlobe,
  FaBuilding,
  FaBook,
  FaLaptopCode,
  FaBriefcase,
  FaPalette,
  FaChartLine,
  FaShieldAlt,
  FaSeedling,
  FaMicroscope,
  FaUsers,
  FaTag,
  FaChevronDown,
  FaTimes,
  FaCrown,
  FaGem,
  FaChevronRight,
  FaFire,
  FaTrophy
} from 'react-icons/fa';
import { 
  FiFilter, 
  FiChevronRight,
  FiStar,
  FiGlobe,
  FiTarget,
  FiAward
} from 'react-icons/fi';
import './UniversitesPartenaires.css';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';

const UniversitesPartenaires = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedSchool, setExpandedSchool] = useState(null);

  const schoolGroups = [
    { 
      id: 'omnes', 
      name: 'OMNES EDUCATION', 
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'Réseau d\'excellence international',
      icon: <FaCrown />
    },
    { 
      id: 'college', 
      name: 'COLLEGE DE PARIS', 
      color: '#f5576c',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      description: 'Innovation et professionnalisation',
      icon: <FaGem />
    },
    { 
      id: 'independent', 
      name: 'ÉCOLES INDÉPENDANTES', 
      color: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      description: 'Expertises spécialisées',
      icon: <FaTrophy />
    }
  ];

  const domains = [
    { id: 'management', name: 'Management', icon: <FaBriefcase />, color: '#667eea' },
    { id: 'finance', name: 'Finance', icon: <FaChartLine />, color: '#43e97b' },
    { id: 'marketing', name: 'Marketing', icon: <FaTag />, color: '#fa709a' },
    { id: 'engineering', name: 'Ingénierie', icon: <FaLaptopCode />, color: '#30cfd0' },
    { id: 'tech', name: 'Technologie', icon: <FaMicroscope />, color: '#764ba2' },
    { id: 'digital', name: 'Digital', icon: <FaGlobe />, color: '#f093fb' },
    { id: 'communication', name: 'Communication', icon: <FaUsers />, color: '#4facfe' },
    { id: 'art', name: 'Arts & Design', icon: <FaPalette />, color: '#fee140' },
    { id: 'politics', name: 'Sciences Politiques', icon: <FaShieldAlt />, color: '#f5576c' },
    { id: 'sustainability', name: 'Développement Durable', icon: <FaSeedling />, color: '#38f9d7' }
  ];

  const schools = [
    {
      id: 1,
      name: 'INSEEC',
      group: 'omnes',
      logo: 'INSEEC',
      description: 'Fondée en 1975, l\'INSEEC est une école de commerce de renommée internationale qui propose un large éventail de programmes en management, finance et marketing. Avec des campus en France et à l\'international, elle forme des leaders capables de relever les défis économiques et sociaux contemporains grâce à une pédagogie alliant théorie et expérience pratique.',
      campuses: ['Paris', 'Lyon', 'Bordeaux', 'Rennes', 'Chambéry', 'Marseille', 'Toulouse', 'Beaune'],
      domains: ['management', 'finance', 'marketing'],
      founded: 1975,
      stars: 4.5,
      students: '8000+',
      highlights: ['Triple accréditation', 'Réseau 60 000 alumni', '180 universités partenaires'],
      color: 'school-1'
    },
    {
      id: 2,
      name: 'ESCE',
      group: 'omnes',
      logo: 'ESCE',
      description: 'L\'ESCE (École Supérieure du Commerce Extérieur) forme depuis 1968 des experts en commerce international. Grâce à des partenariats avec plus de 180 universités à travers le monde et une approche pédagogique basée sur l\'innovation et la pratique, l\'ESCE prépare ses étudiants à exceller dans les environnements multiculturels et compétitifs.',
      campuses: ['Paris', 'Lyon'],
      domains: ['management', 'digital', 'finance'],
      founded: 1968,
      stars: 4.3,
      students: '3000+',
      highlights: ['Programme Grande École', 'Double-diplômes internationaux', 'Labellisée 3 étoiles'],
      color: 'school-2'
    },
    {
      id: 3,
      name: 'EBS Paris',
      group: 'omnes',
      logo: 'EBS PARIS',
      description: 'Depuis 1967, l\'EBS Paris (European Business School) forme des étudiants aux métiers du management, de l\'entrepreneuriat et de l\'innovation. Avec une forte orientation internationale, des cursus en anglais et une communauté d\'anciens élèves influente, l\'EBS Paris prépare ses diplômés à devenir des leaders dans un monde en constante évolution.',
      campuses: ['Paris'],
      domains: ['management', 'digital', 'entrepreneurship'],
      founded: 1967,
      stars: 4.4,
      students: '2000+',
      highlights: ['100% en anglais', 'Incubateur d\'entreprises', 'Réseau corporate fort'],
      color: 'school-3'
    },
    {
      id: 4,
      name: 'ECE',
      group: 'omnes',
      logo: 'ECE',
      description: 'L\'ECE Paris est une école d\'ingénieurs spécialisée dans les nouvelles technologies, la cybersécurité, l\'intelligence artificielle et la data. Elle prépare ses étudiants à devenir des acteurs clés de l\'innovation dans les secteurs les plus dynamiques de l\'économie mondiale.',
      campuses: ['Paris'],
      domains: ['engineering', 'tech', 'digital'],
      founded: 1919,
      stars: 4.6,
      students: '2500+',
      highlights: ['Accréditation CTI', 'Partenariats entreprises', 'Double-compétences'],
      color: 'school-4'
    },
    {
      id: 5,
      name: 'Sup de Pub',
      group: 'omnes',
      logo: 'SUP DE PUB',
      description: 'Créée en 1986, Sup de Pub est une école de référence pour les métiers de la communication, du marketing et de la publicité. Avec un réseau de partenaires dans les grandes agences et entreprises, l\'école offre une immersion totale dans l\'univers créatif et stratégique des marques.',
      campuses: ['Paris', 'Lyon', 'Bordeaux'],
      domains: ['communication', 'marketing', 'art'],
      founded: 1986,
      stars: 4.2,
      students: '1800+',
      highlights: ['Workshops professionnels', 'Portfolio exigent', 'Concours nationaux'],
      color: 'school-5'
    },
    {
      id: 6,
      name: 'HEIP',
      group: 'omnes',
      logo: 'HEIP',
      description: 'HEIP (Hautes Études Internationales et Politiques) est une institution centenaire formant des experts en relations internationales, en sciences politiques et en diplomatie. Avec un programme académique enrichi par des stages et des expériences terrain, HEIP prépare ses diplômés à des carrières prestigieuses.',
      campuses: ['Paris'],
      domains: ['politics', 'management', 'communication'],
      founded: 1899,
      stars: 4.7,
      students: '1200+',
      highlights: ['Carrières internationales', 'Conférences de prestige', 'Réseau diplomatique'],
      color: 'school-6'
    },
    {
      id: 7,
      name: 'Ascencia Business School',
      group: 'college',
      logo: 'Ascensia',
      description: 'Ascencia Business School est une école de commerce axée sur la formation en management et stratégie, avec un fort accent sur l\'international et l\'innovation.',
      campuses: ['Paris'],
      domains: ['management', 'marketing', 'finance'],
      founded: 2002,
      stars: 4.1,
      students: '800+',
      highlights: ['Alternance possible', 'Certifications professionnelles', 'Campus innovant'],
      color: 'school-7'
    },
    {
      id: 8,
      name: 'Keyce Informatique',
      group: 'college',
      logo: 'KEYCE INFORMATIQUE',
      description: 'Keyce Informatique forme les experts de demain en ingénierie informatique grâce à des programmes innovants et professionnalisants, de Bac à Bac+5, intégrant les dernières technologies.',
      campuses: ['Paris', 'Lyon', 'Bordeaux'],
      domains: ['engineering', 'tech', 'digital'],
      founded: 1995,
      stars: 4.3,
      students: '1500+',
      highlights: ['Certifications Microsoft', 'Projets réels clients', 'FabLab intégré'],
      color: 'school-8'
    },
    {
      id: 9,
      name: 'ESTIAM',
      group: 'college',
      logo: 'ESTIAM',
      description: 'ESTIAM est une école spécialisée dans l\'ingénierie informatique, offrant des formations en alternance, avec une expertise en nouvelles technologies.',
      campuses: ['Paris'],
      domains: ['tech', 'digital', 'engineering'],
      founded: 1996,
      stars: 4.0,
      students: '900+',
      highlights: ['100% alternance', 'International au programme', 'Hackathons réguliers'],
      color: 'school-9'
    },
    {
      id: 10,
      name: 'Digital College',
      group: 'college',
      logo: 'DIGITAL COLLEGE',
      description: 'Digital College forme des experts du digital à travers des programmes en marketing numérique, commerce en ligne et transformation digitale.',
      campuses: ['Paris', 'Lyon', 'Bordeaux', 'Nantes'],
      domains: ['digital', 'marketing', 'tech'],
      founded: 2008,
      stars: 4.4,
      students: '2000+',
      highlights: ['Labellisée Grande École du Numérique', 'Écosystème start-up', 'DataLab'],
      color: 'school-10'
    },
    {
      id: 11,
      name: 'École Conte',
      group: 'college',
      logo: 'ECOLE CONTE',
      description: 'L\'École Conte propose des formations en mode, design et textile, en mettant l\'accent sur la créativité et l\'excellence dans le luxe.',
      campuses: ['Paris'],
      domains: ['art', 'design', 'marketing'],
      founded: 1985,
      stars: 4.5,
      students: '600+',
      highlights: ['Showrooms professionnels', 'Partenariats maisons de luxe', 'Ateliers créatifs'],
      color: 'school-11'
    },
    {
      id: 12,
      name: 'KEDGE Business School',
      group: 'independent',
      logo: 'KEDGE',
      description: 'Grande école de commerce reconnue pour son innovation et son ouverture internationale.',
      campuses: ['Bordeaux', 'Marseille', 'Paris', 'Toulon'],
      domains: ['management', 'finance', 'entrepreneurship'],
      founded: 1872,
      stars: 4.8,
      students: '14000+',
      highlights: ['Triple accréditation (AACSB, EQUIS, AMBA)', 'Top 10 français', 'Incubateur top 5'],
      color: 'school-12'
    },
    {
      id: 13,
      name: 'CY Tech',
      group: 'independent',
      logo: 'CY Tech',
      description: 'École d\'ingénieurs spécialisée dans les sciences, l\'informatique et les technologies.',
      campuses: ['Cergy', 'Pau'],
      domains: ['engineering', 'tech', 'science'],
      founded: 1991,
      stars: 4.6,
      students: '3000+',
      highlights: ['Accréditation CTI', 'Recherche de pointe', 'Partenariats industriels forts'],
      color: 'school-13'
    },
    {
      id: 14,
      name: 'Y SCHOOLS',
      group: 'independent',
      logo: 'Y SCHOOLS',
      description: 'Un réseau d\'écoles avec une approche pédagogique innovante et ouverte à l\'international.',
      campuses: ['Troyes'],
      domains: ['design', 'tourism', 'entrepreneurship'],
      founded: 1992,
      stars: 4.2,
      students: '2500+',
      highlights: ['Approche par compétences', 'Campus durable', 'Entrepreneuriat intégré'],
      color: 'school-14'
    },
    {
      id: 15,
      name: 'IMCP',
      group: 'independent',
      logo: 'IMCP',
      description: 'École spécialisée en communication, journalisme et relations internationales.',
      campuses: ['Paris'],
      domains: ['communication', 'journalism', 'politics'],
      founded: 1978,
      stars: 4.3,
      students: '800+',
      highlights: ['Studio TV intégré', 'Partenariats médias', 'Réseau professionnel dense'],
      color: 'school-15'
    },
    {
      id: 16,
      name: 'SUP ECOLIDAIRE',
      group: 'independent',
      logo: 'SUP ECOLIDAIRE',
      description: 'Formations dédiées à l\'économie sociale et solidaire et au développement durable.',
      campuses: ['Paris', 'Lyon', 'Bordeaux'],
      domains: ['sustainability', 'management', 'finance'],
      founded: 2005,
      stars: 4.4,
      students: '1000+',
      highlights: ['Label GreenFlag', 'Projets solidaires', 'RSE intégrée'],
      color: 'school-16'
    }
  ];

  const toggleDomain = (domainId) => {
    setSelectedDomains(prev => 
      prev.includes(domainId) 
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGroup('all');
    setSelectedDomains([]);
  };

  const filteredSchools = useMemo(() => {
    return schools.filter(school => {
      const matchesSearch = searchTerm === '' || 
        school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.domains.some(domain => domains.find(d => d.id === domain)?.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        school.campuses.some(campus => campus.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesGroup = selectedGroup === 'all' || school.group === selectedGroup;

      const matchesDomains = selectedDomains.length === 0 || 
        selectedDomains.some(domain => school.domains.includes(domain));

      return matchesSearch && matchesGroup && matchesDomains;
    });
  }, [searchTerm, selectedGroup, selectedDomains, schools, domains]);

  const getGroupCampuses = (groupId) => {
    const groupSchools = schools.filter(school => school.group === groupId);
    const allCampuses = new Set();
    groupSchools.forEach(school => {
      school.campuses.forEach(campus => allCampuses.add(campus));
    });
    return Array.from(allCampuses).sort();
  };

  return (
    <section className="univ-partners-sectionbig">
      <Header />
      <section className="univ-partners-main-section">
        
        {/* Background Elements */}
        <div className="univ-background-elements">
          <div className="univ-orb univ-orb-1"></div>
          <div className="univ-orb univ-orb-2"></div>
          <div className="univ-orb univ-orb-3"></div>
          <div className="univ-star univ-star-1"></div>
          <div className="univ-star univ-star-2"></div>
          <div className="univ-line univ-line-1"></div>
          <div className="univ-line univ-line-2"></div>
        </div>

        <div className="univ-container">
          {/* Hero Section */}
          <div className="univ-hero-section">
            <div className="univ-hero-content">
              <div className="univ-hero-badge">
                <FiStar className="univ-badge-icon" />
                <span>Réseau d'Excellence</span>
              </div>
              <h1 className="univ-hero-title">
                <span className="univ-title-highlight">Nos Écoles</span> Partenaires
              </h1>
              <div className="univ-hero-subtitle-wrapper">
                <p className="univ-hero-description">
                  Un réseau d'excellence international pour votre réussite académique et professionnelle. 
                  Découvrez nos écoles partenaires prestigieuses en France.
                </p>
                <div className="univ-stats-banner">
                  <div className="stat-item">
                    <FaUniversity className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-number">16</span>
                      <span className="stat-label">Écoles</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <FaMapMarkerAlt className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-number">15+</span>
                      <span className="stat-label">Villes</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <FaGraduationCap className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-number">10</span>
                      <span className="stat-label">Domaines</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="univ-search-section">
            <div className="univ-search-header">
              <h2 className="univ-search-title">
                <FaSearch className="search-title-icon" />
                Trouvez votre école idéale
              </h2>
              <p className="univ-search-subtitle">
                Filtrez selon vos critères et découvrez les opportunités qui vous correspondent
              </p>
            </div>

            <div className="univ-search-container">
              <div className="univ-search-bar">
                <div className="search-bar-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher une école, un domaine, un campus..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="univ-search-input"
                  />
                  {searchTerm && (
                    <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              <div className="univ-filter-controls">
                <button 
                  className="univ-filter-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <div className="filter-toggle-content">
                    <FaFilter className="filter-icon" />
                    <span>Filtres Avancés</span>
                    {(selectedGroup !== 'all' || selectedDomains.length > 0) && (
                      <span className="filter-badge">
                        {selectedDomains.length + (selectedGroup !== 'all' ? 1 : 0)}
                      </span>
                    )}
                    <FaChevronDown className={`filter-arrow ${showFilters ? 'rotate' : ''}`} />
                  </div>
                </button>

                {(selectedGroup !== 'all' || selectedDomains.length > 0) && (
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    <FaTimes />
                    <span>Effacer tous les filtres</span>
                  </button>
                )}
              </div>
            </div>

            {/* Filters Panel */}
            <div className={`univ-filters-panel ${showFilters ? 'show' : ''}`}>
              <div className="filters-panel-header">
                <h3 className="filters-title">
                  <FaFilter className="filters-title-icon" />
                  Affinez votre recherche
                </h3>
              </div>

              {/* Group Filter */}
              <div className="filter-section">
                <h4 className="filter-section-title">
                  <FaBuilding className="section-icon" />
                  Réseaux Partenaires
                </h4>
                <div className="group-buttons-container">
                  <button
                    className={`group-filter-btn ${selectedGroup === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedGroup('all')}
                  >
                    <div className="group-btn-content">
                      <FaUniversity className="group-btn-icon" />
                      <span>Tous les réseaux</span>
                    </div>
                  </button>
                  {schoolGroups.map(group => (
                    <button
                      key={group.id}
                      className={`group-filter-btn ${selectedGroup === group.id ? 'active' : ''}`}
                      onClick={() => setSelectedGroup(group.id)}
                      style={{ 
                        background: selectedGroup === group.id ? group.gradient : 'transparent',
                        borderColor: group.color
                      }}
                    >
                      <div className="group-btn-content">
                        {group.icon}
                        <div className="group-btn-text">
                          <span className="group-btn-name">{group.name}</span>
                          <span className="group-btn-desc">{group.description}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Domains Filter */}
              <div className="filter-section">
                <h4 className="filter-section-title">
                  <FaBook className="section-icon" />
                  Domaines d'Études
                </h4>
                <div className="domain-buttons-container">
                  {domains.map(domain => (
                    <button
                      key={domain.id}
                      className={`domain-filter-btn ${selectedDomains.includes(domain.id) ? 'active' : ''}`}
                      onClick={() => toggleDomain(domain.id)}
                      style={{
                        background: selectedDomains.includes(domain.id) 
                          ? `linear-gradient(135deg, ${domain.color}, rgba(${parseInt(domain.color.slice(1,3), 16)}, ${parseInt(domain.color.slice(3,5), 16)}, ${parseInt(domain.color.slice(5,7), 16)}, 0.8))`
                          : 'transparent',
                        borderColor: domain.color
                      }}
                    >
                      <div className="domain-btn-content">
                        <span className="domain-btn-icon">{domain.icon}</span>
                        <span className="domain-btn-name">{domain.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {(selectedGroup !== 'all' || selectedDomains.length > 0) && (
              <div className="univ-active-filters">
                <div className="active-filters-header">
                  <span className="active-filters-label">Filtres appliqués :</span>
                </div>
                <div className="active-filters-tags">
                  {selectedGroup !== 'all' && (
                    <div className="active-filter-tag" style={{ background: schoolGroups.find(g => g.id === selectedGroup)?.gradient }}>
                      <span className="filter-tag-content">
                        <FaBuilding className="filter-tag-icon" />
                        {schoolGroups.find(g => g.id === selectedGroup)?.name}
                      </span>
                      <button className="remove-filter-btn" onClick={() => setSelectedGroup('all')}>
                        <FaTimes />
                      </button>
                    </div>
                  )}
                  {selectedDomains.map(domainId => {
                    const domain = domains.find(d => d.id === domainId);
                    return (
                      <div key={domainId} className="active-filter-tag" style={{ background: domain?.color + '20' }}>
                        <span className="filter-tag-content">
                          {domain?.icon}
                          {domain?.name}
                        </span>
                        <button className="remove-filter-btn" onClick={() => toggleDomain(domainId)}>
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="univ-results-section">
            <div className="univ-results-header">
              <div className="results-info">
                <h3 className="results-title">
                  Écoles Disponibles
                  <span className="results-count">({filteredSchools.length})</span>
                </h3>
                <p className="results-subtitle">
                  {filteredSchools.length === schools.length 
                    ? 'Toutes nos écoles partenaires'
                    : `${filteredSchools.length} école${filteredSchools.length !== 1 ? 's' : ''} correspond${filteredSchools.length !== 1 ? 'ent' : ''} à vos critères`
                  }
                </p>
              </div>
            </div>

            {/* Schools Grid */}
            <div className="univ-schools-grid">
              {filteredSchools.map(school => {
                const group = schoolGroups.find(g => g.id === school.group);
                const isExpanded = expandedSchool === school.id;
                
                return (
                  <div key={school.id} className={`univ-school-card ${school.color}`}>
                    <div className="school-card-glow"></div>
                    <div className="school-card-header" style={{ background: group?.gradient }}>
                      <div className="school-header-content">
                        <div className="school-logo-container">
                          <div className="school-logo-wrapper">
                            <FaUniversity className="school-logo-icon" />
                          </div>
                          <div className="school-logo-text">
                            <span className="logo-acronym">{school.logo.split(' ')[0]}</span>
                            {school.logo.split(' ').slice(1).join(' ')}
                          </div>
                        </div>
                        <div className="school-rating-container">
                          <div className="school-rating">
                            <FiStar className="star-icon" />
                            <span className="rating-value">{school.stars}</span>
                            <span className="rating-max">/5</span>
                          </div>
                          <div className="school-group-badge" style={{ background: group?.color }}>
                            {group?.icon}
                            <span>{group?.name.split(' ')[0]}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="school-card-body">
                      <h3 className="school-name">{school.name}</h3>
                      
                      <div className="school-meta-info">
                        <div className="meta-row">
                          <div className="meta-item">
                            <FaMapMarkerAlt className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Campus</span>
                              <div className="campuses-tags">
                                {school.campuses.map((campus, idx) => (
                                  <span key={idx} className="campus-tag">
                                    {campus}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="meta-row">
                          <div className="meta-item">
                            <FaGraduationCap className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Fondée en</span>
                              <span className="founded-year">{school.founded}</span>
                            </div>
                          </div>
                          <div className="meta-item">
                            <FaUsers className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Étudiants</span>
                              <span className="students-count">{school.students}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="school-description-container">
                        <p className="school-description">
                          {isExpanded ? school.description : `${school.description.substring(0, 150)}...`}
                        </p>
                        <button 
                          className="read-more-btn"
                          onClick={() => setExpandedSchool(isExpanded ? null : school.id)}
                        >
                          {isExpanded ? 'Lire moins' : 'Lire plus'}
                          <FaChevronDown className={`read-more-arrow ${isExpanded ? 'rotate' : ''}`} />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="school-highlights">
                          <h4 className="highlights-title">
                            <FaFire className="highlights-icon" />
                            Points forts
                          </h4>
                          <div className="highlights-list">
                            {school.highlights.map((highlight, idx) => (
                              <div key={idx} className="highlight-item">
                                <FiChevronRight className="highlight-arrow" />
                                <span>{highlight}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="school-domains-section">
                        <h4 className="domains-title">
                          <FaBook className="domains-icon" />
                          Domaines enseignés
                        </h4>
                        <div className="domains-tags">
                          {school.domains.map(domainId => {
                            const domain = domains.find(d => d.id === domainId);
                            return (
                              <span key={domainId} className="domain-tag" style={{ 
                                background: domain?.color + '15',
                                color: domain?.color,
                                borderColor: domain?.color
                              }}>
                                {domain?.icon}
                                {domain?.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="school-card-footer">
                     
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredSchools.length === 0 && (
              <div className="univ-no-results">
                <div className="no-results-content">
                  <FaUniversity className="no-results-icon" />
                  <h3 className="no-results-title">Aucune école trouvée</h3>
                  <p className="no-results-message">
                    Aucune école ne correspond à vos critères de recherche. 
                    Essayez de modifier vos filtres ou votre recherche.
                  </p>
                  <button className="reset-filters-btn" onClick={clearFilters}>
                    Réinitialiser tous les filtres
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Groups Summary */}
          <div className="univ-groups-summary">
            <div className="groups-summary-header">
              <h2 className="groups-summary-title">
                <FaBuilding className="summary-title-icon" />
                Nos Réseaux Partenaires
              </h2>
              <p className="groups-summary-subtitle">
                Découvrez les écosystèmes académiques d'excellence
              </p>
            </div>

            <div className="groups-summary-grid">
              {schoolGroups.map(group => {
                const groupSchools = schools.filter(s => s.group === group.id);
                const campuses = getGroupCampuses(group.id);
                
                return (
                  <div key={group.id} className="group-summary-card">
                    <div className="group-summary-header" style={{ background: group.gradient }}>
                      <div className="group-header-content">
                        <div className="group-icon-wrapper">
                          {group.icon}
                        </div>
                        <div className="group-header-text">
                          <h3 className="group-name">{group.name}</h3>
                          <p className="group-description">{group.description}</p>
                        </div>
                      </div>
                      <div className="group-schools-count">
                        <span className="count-number">{groupSchools.length}</span>
                        <span className="count-label">écoles</span>
                      </div>
                    </div>

                    <div className="group-summary-body">
                      <div className="group-details">
                        <div className="detail-section">
                          <h4 className="detail-section-title">
                            <FaMapMarkerAlt className="detail-icon" />
                            Présence nationale
                          </h4>
                          <div className="campus-list">
                            {campuses.map((campus, idx) => (
                              <span key={idx} className="campus-item">
                                {campus}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4 className="detail-section-title">
                            <FaGraduationCap className="detail-icon" />
                            Écoles du réseau
                          </h4>
                          <div className="school-list">
                            {groupSchools.map(school => (
                              <div key={school.id} className="school-list-item">
                                <FaUniversity className="school-item-icon" />
                                <span className="school-item-name">{school.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="group-stats">
                        <div className="group-stat">
                          <FiTarget className="stat-icon" />
                          <div className="stat-content">
                            <span className="stat-number">{groupSchools.reduce((sum, s) => sum + parseInt(s.students.replace('+', '')), 0)}+</span>
                            <span className="stat-label">étudiants</span>
                          </div>
                        </div>
                        <div className="group-stat">
                          <FiAward className="stat-icon" />
                          <div className="stat-content">
                            <span className="stat-number">{(groupSchools.reduce((sum, s) => sum + s.stars, 0) / groupSchools.length).toFixed(1)}</span>
                            <span className="stat-label">moyenne /5</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="univ-cta-section">
            <div className="univ-cta-card">
              <div className="univ-cta-glow"></div>
              <div className="univ-cta-content">
                <div className="cta-header">
                  <div className="cta-icon-wrapper">
                    <FaStar className="cta-icon" />
                  </div>
                  <div className="cta-title-wrapper">
                    <h2 className="cta-title">Prêt à concrétiser votre projet ?</h2>
                    <p className="cta-subtitle">
                      Notre équipe d'experts vous accompagne dans votre démarche d'admission
                    </p>
                  </div>
                </div>
                
                <div className="cta-body">
                  <p className="cta-description">
                    Choisissez l'école qui correspond parfaitement à votre projet professionnel. 
                    Nous vous guidons à chaque étape : orientation, admission, logement et intégration.
                  </p>
                  
                  <div className="cta-buttons">
                    <button className="cta-button primary">
                      <FaUsers className="button-icon" />
                      <span>Demander un accompagnement</span>
                      <FiChevronRight className="button-arrow" />
                    </button>
                    <button className="cta-button secondary">
                      <FaGraduationCap className="button-icon" />
                      <span>Comparer les écoles</span>
                      <FiChevronRight className="button-arrow" />
                    </button>
                  </div>
                </div>
                
                <div className="cta-footer">
                  <div className="cta-guarantee">
                    <FaShieldAlt className="guarantee-icon" />
                    <span>Accompagnement personnalisé garanti</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </section>
  );
};

export default UniversitesPartenaires;